import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const ERAS = ["Territory", "Golden", "New Generation", "Attitude", "Ruthless Aggression", "PG", "Reality", "Modern"];

const csv = (v) => (Array.isArray(v) ? v.join(", ") : "");
const arr = (v) => v.split(",").map((s) => s.trim()).filter(Boolean);

export default function AdminWrestlerEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const editing = !!id;
  const [form, setForm] = useState({
    primary_name: "", former_names: "", nicknames: "",
    tag_teams: "", factions: "", era: "", active: true,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!editing) return;
    api.get(`/admin/wrestlers/${id}`).then((r) => {
      const d = r.data;
      setForm({
        primary_name: d.primary_name || "",
        former_names: csv(d.former_names), nicknames: csv(d.nicknames),
        tag_teams: csv(d.tag_teams), factions: csv(d.factions),
        era: d.era || "", active: !!d.active,
      });
    });
  }, [id, editing]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.primary_name.trim()) { toast.error("Primary name is required"); return; }
    setBusy(true);
    try {
      const body = {
        primary_name: form.primary_name.trim(),
        former_names: arr(form.former_names),
        nicknames: arr(form.nicknames),
        tag_teams: arr(form.tag_teams),
        factions: arr(form.factions),
        era: form.era || null,
        active: form.active,
      };
      if (editing) await api.patch(`/admin/wrestlers/${id}`, body);
      else await api.post("/admin/wrestlers", body);
      toast.success("Saved");
      nav("/admin/wrestlers");
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12">
      <div className="text-xs font-mono uppercase tracking-widest text-red-500 mb-1">Roster</div>
      <h1 className="font-display text-3xl sm:text-5xl font-black uppercase">
        {editing ? "Edit" : "New"} Wrestler
      </h1>

      <div className="mt-6 space-y-5">
        <FieldRow label="Primary display name">
          <Input value={form.primary_name} onChange={(e) => upd("primary_name", e.target.value)}
            data-testid="w-primary" className="bg-slate-950 border-slate-700" />
        </FieldRow>

        <FieldRow label="Former ring names (comma separated)">
          <Input value={form.former_names} onChange={(e) => upd("former_names", e.target.value)}
            data-testid="w-former" placeholder="e.g. Stunning Steve Austin, The Ringmaster"
            className="bg-slate-950 border-slate-700" />
        </FieldRow>

        <FieldRow label="Nicknames (comma separated)">
          <Input value={form.nicknames} onChange={(e) => upd("nicknames", e.target.value)}
            data-testid="w-nick" placeholder="e.g. The Rattlesnake, Stone Cold"
            className="bg-slate-950 border-slate-700" />
        </FieldRow>

        <FieldRow label="Tag teams (comma separated)">
          <Input value={form.tag_teams} onChange={(e) => upd("tag_teams", e.target.value)}
            data-testid="w-team" placeholder="e.g. The Hollywood Blondes"
            className="bg-slate-950 border-slate-700" />
        </FieldRow>

        <FieldRow label="Factions (comma separated)">
          <Input value={form.factions} onChange={(e) => upd("factions", e.target.value)}
            data-testid="w-faction" placeholder="e.g. The Alliance"
            className="bg-slate-950 border-slate-700" />
        </FieldRow>

        <div className="grid grid-cols-2 gap-4">
          <FieldRow label="Era">
            <Select value={form.era || ""} onValueChange={(v) => upd("era", v)}>
              <SelectTrigger data-testid="w-era" className="bg-slate-950 border-slate-700">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>{ERAS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
            </Select>
          </FieldRow>
          <FieldRow label="Active roster">
            <div className="flex items-center gap-3 h-10 pl-2">
              <Switch checked={form.active} onCheckedChange={(v) => upd("active", v)}
                      data-testid="w-active" />
              <span className="text-sm text-slate-400">{form.active ? "Active" : "Retired"}</span>
            </div>
          </FieldRow>
        </div>

        <div className="flex gap-3 pt-2">
          <Button onClick={save} disabled={busy} data-testid="w-save"
            className="bg-red-600 hover:bg-red-500 text-white font-bold uppercase">Save</Button>
          <Button variant="ghost" onClick={() => nav("/admin/wrestlers")} data-testid="w-cancel">Cancel</Button>
        </div>
      </div>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-400 font-mono">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
